import { isProPlan, planLabel } from '../lib/plans';
import { safeLocalStorage } from '../lib/safeStorage';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${safeLocalStorage.getItem('mm_access_token')}` } });
const n = (v) => (v ?? 0).toLocaleString('tr-TR');
const pct = (v) => `${Math.round((v ?? 0) * 100)}%`;

const Stat = ({ label, value, testId }) => (
  <div className="bg-[#F8FAFF] border border-[#E6EEFF] rounded-xl p-3">
    <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</p>
    <p className="font-serif text-xl text-zinc-900 tabular-nums" data-testid={testId}>{value}</p>
  </div>
);

const Table = ({ rows, first, firstLabel, testId }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid={testId}>
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 border-b border-zinc-100">
          <th className="py-2 pr-3">{firstLabel}</th><th className="py-2 pr-3 text-right">Çağrı</th><th className="py-2 pr-3 text-right">Cache</th>
          <th className="py-2 pr-3 text-right">Input</th><th className="py-2 pr-3 text-right">Output</th><th className="py-2 text-right">Hata</th>
        </tr>
      </thead>
      <tbody>
        {(!Array.isArray(rows) || rows.length === 0) && <tr><td colSpan={6} className="py-3 text-zinc-400">Kayıt yok</td></tr>}
        {(Array.isArray(rows) ? rows : []).map((r, i) => (
          <tr key={i} className="border-b border-zinc-50 tabular-nums">
            <td className="py-2 pr-3 text-zinc-800">{first(r)}</td>
            <td className="py-2 pr-3 text-right">{n(r.calls)}</td>
            <td className="py-2 pr-3 text-right">{n(r.cached)} <span className="text-zinc-400">({pct(r.cache_hit_rate)})</span></td>
            <td className="py-2 pr-3 text-right">{n(r.input_tokens)}</td>
            <td className="py-2 pr-3 text-right">{n(r.output_tokens)}</td>
            <td className="py-2 text-right">{n(r.failed)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const AdminAiUsage = () => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/admin/ai-usage/summary?days=${days}`, authHeaders()).then(r => setData(r.data)).catch(() => setData({ error: true }));
  }, [days]);
  const t = data?.totals || {};
  return (
    <section className="bg-white border border-zinc-100 rounded-2xl p-6 space-y-5" data-testid="admin-ai-usage">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-9 h-9 rounded-xl bg-[#E6EEFF] flex items-center justify-center"><Activity size={16} className="text-[#1E3A8A]" /></div>
        <div className="flex-1">
          <h2 className="text-base md:text-lg font-semibold text-zinc-900">AI Kullanımı</h2>
          <p className="text-xs text-zinc-500">ai_usage kayıtları — kullanıcı / görev / token / cache-hit</p>
        </div>
        <select value={days} onChange={e => setDays(Number(e.target.value))} className="text-sm border border-zinc-200 rounded-lg px-2 py-1" data-testid="ai-usage-days">
          {[1, 7, 30].map(d => <option key={d} value={d}>Son {d} gün</option>)}
        </select>
      </div>
      {data?.error && <p className="text-sm text-red-600">Özet alınamadı.</p>}
      {data && !data.error && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Stat label="Çağrı" value={n(t.calls)} testId="ai-usage-calls" />
            <Stat label="Cache hit" value={`${n(t.cached)} (${pct(t.cache_hit_rate)})`} testId="ai-usage-cache" />
            <Stat label="Input token" value={n(t.input_tokens)} testId="ai-usage-input" />
            <Stat label="Output token" value={n(t.output_tokens)} testId="ai-usage-output" />
            <Stat label="Hata" value={n(t.failed)} testId="ai-usage-failed" />
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div><p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Görev</p>
              <Table rows={data.by_task} firstLabel="Task" first={r => r.task || '—'} testId="ai-usage-by-task" /></div>
            <div><p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Model</p>
              <Table rows={data.by_model} firstLabel="Model" first={r => <span className="font-mono text-xs">{r.model}</span>} testId="ai-usage-by-model" /></div>
          </div>
          <div><p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Kullanıcı (top 25)</p>
            <Table rows={data.by_user} firstLabel="Kullanıcı" testId="ai-usage-by-user"
              first={r => <span>{r.email || r.user_id || 'anonim'} {r.plan && <span className={`ml-1 text-[10px] uppercase font-semibold ${isProPlan(r.plan) ? 'text-[#1E3A8A]' : 'text-zinc-400'}`}>{planLabel(r.plan)}</span>}</span>} /></div>
        </>
      )}
    </section>
  );
};
